import type { Point3 } from '../detection/types';

/** Face Landmarker : 468 points du maillage + 10 points d'iris. */
export const MESH_POINTS = 478;

export interface MeshFrame {
  /** Numéro croissant côté émetteur (le canal n'est pas ordonné). */
  readonly seq: number;
  /** Horloge de l'émetteur (ms). */
  readonly t: number;
  /** Points normalisés ; vide = pas de visage. */
  readonly points: readonly Point3[];
}

// Format v1 (little-endian) :
// [0] 'B'  [1] version  [2..5] seq u32  [6..13] t f64  [14..15] n u16  puis n × (x u16, y u16)
const MAGIC = 0x42;
const VERSION = 1;
const HEADER = 16;
const BYTES_PER_POINT = 4;
// x, y quantifiés sur [-0.5 ; 1.5] (MediaPipe extrapole un peu hors du cadre).
const MIN = -0.5;
const RANGE = 2;
const MAX_Q = 0xffff;

const quantize = (v: number): number => {
  const k = Number.isFinite(v) ? (v - MIN) / RANGE : 0;
  return Math.round(Math.min(1, Math.max(0, k)) * MAX_Q);
};
const dequantize = (q: number): number => MIN + (q / MAX_Q) * RANGE;

/** Encode une frame du maillage (z non transmis : rendu 2D). */
export function encodeMeshFrame(frame: MeshFrame): ArrayBuffer {
  const n = frame.points.length;
  if (n > MESH_POINTS) throw new Error(`trop de points : ${n}`);
  const buffer = new ArrayBuffer(HEADER + n * BYTES_PER_POINT);
  const view = new DataView(buffer);
  view.setUint8(0, MAGIC);
  view.setUint8(1, VERSION);
  view.setUint32(2, frame.seq >>> 0, true);
  view.setFloat64(6, frame.t, true);
  view.setUint16(14, n, true);
  frame.points.forEach((p, i) => {
    const offset = HEADER + i * BYTES_PER_POINT;
    view.setUint16(offset, quantize(p.x), true);
    view.setUint16(offset + 2, quantize(p.y), true);
  });
  return buffer;
}

/** Décode et valide une frame reçue ; `null` si invalide. Ne lève jamais d'exception. */
export function decodeMeshFrame(buffer: ArrayBuffer): MeshFrame | null {
  if (buffer.byteLength < HEADER) return null;
  const view = new DataView(buffer);
  if (view.getUint8(0) !== MAGIC || view.getUint8(1) !== VERSION) return null;
  const seq = view.getUint32(2, true);
  const t = view.getFloat64(6, true);
  const n = view.getUint16(14, true);
  if (!Number.isFinite(t) || n > MESH_POINTS) return null;
  if (buffer.byteLength !== HEADER + n * BYTES_PER_POINT) return null;
  const points: Point3[] = [];
  for (let i = 0; i < n; i++) {
    const offset = HEADER + i * BYTES_PER_POINT;
    points.push({
      x: dequantize(view.getUint16(offset, true)),
      y: dequantize(view.getUint16(offset + 2, true)),
      z: 0,
    });
  }
  return { seq, t, points };
}
